/**
 * Hedef hayvan türleri (F3B-12). Tek kaynak: ürün formu, içe/dışa aktarma, vitrin süzgeci, karşılaştırma,
 * schema.org ve Merchant alanları bu listeyi kullanır. Veritabanında `products.species` sütunu bu
 * değerlerle sınırlıdır (CHECK, migration 0022). Sıra vitrinde ve panelde gösterim sırasıdır.
 */
export const SPECIES = ['cat', 'dog', 'bird', 'rodent', 'rabbit', 'reptile', 'exotic'] as const;
export type Species = (typeof SPECIES)[number];

export const isSpecies = (v: unknown): v is Species =>
  typeof v === 'string' && (SPECIES as readonly string[]).includes(v);

const fold = (s: string) =>
  s
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/ı/g, 'i')
    .replace(/[^a-z0-9]/g, '');

/** İçe aktarmada tanınan adlar (Türkçe/İngilizce, tekil/çoğul, yaygın tür adları). */
const SYNONYMS: Record<Species, string[]> = {
  cat: ['kedi', 'kediler', 'cat', 'cats', 'feline'],
  dog: ['köpek', 'köpekler', 'dog', 'dogs', 'canine'],
  bird: ['kuş', 'kuşlar', 'bird', 'birds', 'papağan', 'muhabbet kuşu', 'kanarya', 'parrot', 'avian'],
  rodent: [
    'kemirgen',
    'kemirgenler',
    'hamster',
    'kobay',
    'ginepig',
    'guinea pig',
    'gerbil',
    'fare',
    'sıçan',
    'şinşilla',
    'chinchilla',
    'rodent',
    'rodents',
  ],
  rabbit: ['tavşan', 'tavşanlar', 'rabbit', 'rabbits'],
  reptile: [
    'sürüngen',
    'sürüngenler',
    'kaplumbağa',
    'su kaplumbağası',
    'iguana',
    'kertenkele',
    'yılan',
    'reptile',
    'reptiles',
    'turtle',
    'tortoise',
  ],
  exotic: ['egzotik', 'diğer egzotik', 'diğer', 'exotic', 'other exotic', 'other'],
};
const LOOKUP = new Map<string, Species>(
  (Object.entries(SYNONYMS) as Array<[Species, string[]]>).flatMap(([k, names]) =>
    names.map((n) => [fold(n), k]),
  ),
);

/**
 * İçe aktarma hücresindeki tür listesini çözer: "kedi, köpek", "Kuş / Tavşan", "hepsi". "kedi-köpek"
 * eski şablon yazımıdır (kedi + köpek). Tanınmayan bir ad varsa null (satır hatası).
 */
export function parseSpeciesList(raw: string): Species[] | null {
  const out = new Set<Species>();
  for (const part of raw.split(/[,;/|]+/)) {
    const p = fold(part);
    if (!p) continue;
    if (['hepsi', 'tumu', 'tumturler', 'all'].includes(p)) {
      for (const s of SPECIES) out.add(s);
      continue;
    }
    if (p === 'kedikopek' || p === 'catdog') {
      out.add('cat');
      out.add('dog');
      continue;
    }
    const s = LOOKUP.get(p);
    if (!s) return null;
    out.add(s);
  }
  return SPECIES.filter((s) => out.has(s));
}

/** Dışa aktarmadaki Türkçe adlar (içe aktarmada geri tanınır). */
export const SPECIES_EXPORT_NAMES: Record<Species, string> = {
  cat: 'kedi',
  dog: 'köpek',
  bird: 'kuş',
  rodent: 'kemirgen',
  rabbit: 'tavşan',
  reptile: 'sürüngen',
  exotic: 'egzotik',
};
